<template>
    <div class="module">
        <header class="module__header">
            <h1>Feature Flags</h1>
        </header>

        <section
            v-if="canViewGlobalFlags"
            class="module__body"
            data-test-id="global-feature-flags-section"
        >
            <h2>Global Flags</h2>
            <p class="section-help">
                Global flags apply to every organization. Changing one overrides any organization-specific setting.
            </p>

            <p
                v-if="isLoading"
                data-test-id="global-feature-flags-loading"
            >
                Loading feature flags...
            </p>

            <p
                v-else-if="loadError"
                class="section-error"
                data-test-id="global-feature-flags-error"
            >
                {{ loadError }}
            </p>

            <div
                v-else
                class="flag-list"
                data-test-id="global-feature-flags-list"
            >
                <div
                    v-for="flag in flags"
                    :key="flag.name"
                    class="flag-row"
                    :data-test-id="`global-flag-row-${flag.name}`"
                >
                    <slider-toggle-field
                        :data-test-id="`global-flag-${flag.name}`"
                        :focus-key="`global-flag-${flag.name}`"
                        :name="flag.name"
                        :label="humanize(flag.name)"
                        :description="flag.description"
                        :value="flag.enabled"
                        :disabled="isSaving || !canUpdate"
                        @update="confirmToggle(flag, $event)"
                    />

                    <organization-override-list
                        v-if="canViewOrganizationFlags"
                        :flag="flag"
                        :flag-label="humanize(flag.name)"
                        :global-value="Boolean(globalFlagValues[flag.name])"
                        :organizations="matrix.organizations"
                        :is-expanded="isExpanded(flag)"
                        :can-update="canUpdateOrganizationFlags"
                        :is-busy="isSaving || isBusy"
                        :saving-organization-ids="savingOrganizationIds(flag)"
                        :is-loading="isMatrixLoading"
                        :has-loaded="hasMatrix"
                        :load-error="matrixError"
                        @toggle-expanded="toggleExpanded(flag)"
                        @delete="confirmDeleteOverride(flagFor(flag.name), $event)"
                        @add="confirmAddOverride(flagFor(flag.name), $event)"
                    />
                </div>
            </div>
        </section>

        <section
            v-if="canViewOrganizationFlags"
            class="module__body"
            data-test-id="organization-flags-section"
        >
            <h2>Organization Flags</h2>
            <p class="section-help">See and edit feature flags for a specific organization.</p>

            <label
                class="organization-filter__label"
                for="organization-flags-organization"
            >
                Organization
            </label>
            <organization-selector
                class="organization-filter"
                allow-clear
                input-id="organization-flags-organization"
                placeholder="Select Organization"
                :organization-id="selectedOrganizationId"
                @update="selectedOrganizationId = $event"
            />

            <organization-flags-for-org
                :columns="organizationFlagColumns"
                :organization="selectedOrganization"
                :saving-flag-names="savingFlagNames"
                :is-loading="isMatrixPending"
                :load-error="matrixError"
                :can-update="canUpdateOrganizationFlags && !isSaving && !isBusy"
                @toggle="confirmSelectedOrganizationFlag"
                @delete="confirmDeleteSelectedOverride"
            />
        </section>
    </div>
</template>

<script>
import { closeModal, openModal } from 'jenesius-vue-modal'
import ConfirmModal from '@/components/Modal/ConfirmModal.vue'
import OrganizationSelector from '@/components/OrganizationSelector.vue'
import SliderToggleField from '@/components/SliderToggleField.vue'
import OrganizationFlagsForOrg from './OrganizationFlagsForOrg.vue'
import OrganizationOverrideList from './OrganizationOverrideList.vue'
import overridingOrganizations from './overrides'
import Permissions from '@/mixins/permissions'
import canToast from '@/mixins/canToast'
import store from '@/store'

export default {
    components: {
        OrganizationFlagsForOrg,
        OrganizationOverrideList,
        OrganizationSelector,
        SliderToggleField,
    },
    mixins: [Permissions, canToast],

    data() {
        return {
            flags: [],
            isLoading: true,
            isSaving: false,
            loadError: null,
            matrix: { flags: {}, globals: {}, organizations: [] },
            matrixError: null,
            isMatrixLoading: true,
            hasMatrix: false,
            expandedFlags: [],
            selectedOrganizationId: null,
            savingCells: {},
        }
    },

    computed: {
        canViewGlobalFlags() {
            return this.can('view global feature flags')
        },

        canUpdate() {
            return this.can('update global feature flags')
        },

        canViewOrganizationFlags() {
            return this.can('view organization feature flags')
        },

        canUpdateOrganizationFlags() {
            return this.can('update organization feature flags')
        },

        // An organization write needs both payloads: the global flags for the
        // value an override diverges from, and the matrix for the flag id and
        // the row it writes to. A click landing between the two responses
        // finds an empty matrix and does nothing, silently.
        //
        // Each half counts only when the user's permissions fetched it — an
        // unfetched half keeps its loading flag at its initial true, pinning
        // isBusy on and disabling every write the user can see.
        isBusy() {
            return Boolean(
                (this.canViewGlobalFlags && (this.isLoading || this.loadError)) ||
                    (this.canViewOrganizationFlags && (this.isMatrixLoading || this.matrixError))
            )
        },

        // Loading is reported only while nothing has ever loaded, so the
        // refetch after a global write leaves the lists on screen — disabled
        // by isBusy — instead of blanking them. The matrix reports only
        // overriding organizations, so an empty array is a normal steady
        // state, not a first load that never landed.
        isMatrixPending() {
            return this.isMatrixLoading && !this.hasMatrix
        },

        // Columns come from the organization endpoint's name map, not the
        // global flags array. This keeps section 2 independent of a
        // separately-loaded payload.
        organizationFlagColumns() {
            return Object.keys(this.matrix.flags).map((baseName) => ({
                baseName,
                label: this.humanize(baseName),
            }))
        },

        // A section reads values out of the payload it writes against. The
        // global toggles write global flags, so they read `flag.enabled` from
        // the global endpoint, which is also where the descriptions live.
        // Everything organization-scoped writes organization rows, so it
        // reads the global value from the matrix: that is the baseline the
        // endpoint applied when deciding which organizations diverge. The
        // override lists sit inside section 1 on screen but write
        // organization rows, so they read from here too.
        globalFlagValues() {
            return this.matrix.globals
        },

        selectedOrganization() {
            if (this.selectedOrganizationId === null) {
                return null
            }

            return this.organizationFor(this.selectedOrganizationId)
        },

        // Section 2 acts on one organization, so it needs the per-flag view of
        // the same savingCells state the override lists read per-organization.
        savingFlagNames() {
            return Object.keys(this.savingCells).filter((baseName) =>
                (this.savingCells[baseName] ?? []).includes(this.selectedOrganizationId)
            )
        },
    },

    mounted() {
        if (this.canViewGlobalFlags) {
            this.loadFlags()
        }

        if (this.canViewOrganizationFlags) {
            this.loadOrganizationFlags()
        }
    },

    methods: {
        loadOrganizationFlags() {
            this.matrixError = null
            this.isMatrixLoading = true

            return this.$http
                .get('feature-flags/organizations')
                .then(({ data }) => {
                    // Merged over the empty shape: every read path indexes
                    // into these three keys unguarded, so a payload missing
                    // one would throw or render every derived toggle off.
                    this.matrix = { flags: {}, globals: {}, organizations: [], ...data }
                    this.hasMatrix = true
                })
                .catch(() => {
                    const message = 'There was a problem loading organization feature flags.'

                    this.matrix = { flags: {}, globals: {}, organizations: [] }
                    this.matrixError = message
                    this.toast(message, 'error')
                })
                .finally(() => {
                    this.isMatrixLoading = false
                })
        },

        isExpanded(flag) {
            return this.expandedFlags.includes(flag.name)
        },

        toggleExpanded(flag) {
            if (this.isExpanded(flag)) {
                this.expandedFlags = this.expandedFlags.filter((name) => name !== flag.name)

                return
            }

            this.expandedFlags = [...this.expandedFlags, flag.name]
        },

        savingOrganizationIds(flag) {
            return this.savingCells[flag.name] ?? []
        },

        overrideCount(flag) {
            return overridingOrganizations(this.matrix.organizations, flag.name).length
        },

        findOrganization(organizationId) {
            return this.matrix.organizations.find((candidate) => candidate.id === organizationId)
        },

        // A derived row needs the organizations store to know the id, so a
        // miss is reachable when that request failed or has not landed. Every
        // write path funnels its miss through here: a click that opens no
        // prompt and says nothing reads as a broken page.
        reportMissingOrganization() {
            this.toast('That organization could not be found. Reload the page and try again.', 'error')
        },

        // The matrix reports only organizations that override something.
        // Every other organization inherits the globals, so its row is
        // derived: the values from the payload, the name from the
        // organizations store. Null means the store has no such id.
        organizationFor(organizationId) {
            const overriding = this.findOrganization(organizationId)

            if (overriding) {
                return overriding
            }

            const known = store.getters['organizations/allOrganizations'].find(
                (candidate) => candidate.id === organizationId
            )

            if (!known) {
                return null
            }

            return {
                id: known.id,
                name: known.name,
                flags: { ...this.matrix.globals },
                overrides: [],
            }
        },

        // Write paths mutate the row they are handed, so a derived row has to
        // join matrix.organizations first: mutating the object
        // organizationFor returns is discarded on the next recompute, taking
        // the optimistic update and its rollback with it. The row is read back
        // out of the array so the mutation lands on the reactive proxy.
        materializeOrganization(organizationId) {
            const existing = this.findOrganization(organizationId)

            if (existing) {
                return existing
            }

            const derived = this.organizationFor(organizationId)

            if (!derived) {
                return null
            }

            this.matrix.organizations.push(derived)

            return this.findOrganization(organizationId)
        },

        overrideStateOf(organization, baseName) {
            return {
                value: organization.flags[baseName],
                isOverride: organization.overrides.includes(baseName),
            }
        },

        // Both fields move together. A failed write restores both through this
        // same helper, so the value and the override set cannot drift apart on
        // an error path.
        applyOverrideState(organization, baseName, { value, isOverride }) {
            organization.flags[baseName] = value
            organization.overrides = isOverride
                ? [...new Set([...organization.overrides, baseName])]
                : organization.overrides.filter((name) => name !== baseName)
        },

        // The one place an organization-scoped path gets a flag to write
        // with: base name plus the matrix's global value, which is the value
        // an add diverges from and a delete reverts to. Section 1's override
        // lists come through here too — the flag object they are handed
        // carries the global endpoint's value, not the matrix's baseline.
        flagFor(baseName) {
            return {
                name: baseName,
                enabled: Boolean(this.globalFlagValues[baseName]),
            }
        },

        // A successful write can destroy or disable the control that opened
        // the prompt: the deleted row leaves the list, a written flag's toggle
        // is disabled for the duration. ConfirmModal declines to restore focus
        // to a control in that state, leaving focus on <body> with Tab
        // restarting at the left-hand nav — so place it on the nearest
        // surviving control once the write has settled.
        //
        // Controls are addressed by data-focus-key, never by data-test-id: a
        // test id must stay free to change without breaking focus management.
        focusFirstAvailable(focusKeys) {
            return this.$nextTick().then(() => {
                // Only rescue focus that has actually fallen off the page. If
                // the user tabbed away while the request was in flight, focus
                // is theirs to keep.
                if (document.activeElement && document.activeElement !== document.body) {
                    return
                }

                const found = focusKeys
                    .map((focusKey) => this.$el.querySelector(`[data-focus-key="${focusKey}"]`))
                    .find((element) => element && !element.disabled)

                found?.focus()
            })
        },

        // Read before the delete lands, while the organization is still in
        // the list: the row after it, else the row before, else the controls
        // below the list. Shares one ordering with the list that renders those
        // rows, so the neighbour named here is the neighbour on screen.
        neighbourAfterDelete(flag, organizationId) {
            const overriding = overridingOrganizations(this.matrix.organizations, flag.name)

            const index = overriding.findIndex((organization) => organization.id === organizationId)
            const neighbour = overriding[index + 1] ?? overriding[index - 1]

            return [
                ...(neighbour ? [`override-delete-${flag.name}-${neighbour.id}`] : []),
                `override-add-toggle-${flag.name}`,
                `overrides-header-${flag.name}`,
            ]
        },

        confirmDeleteOverride(flag, { organizationId, focusAfter = null }) {
            const organization = this.organizationFor(organizationId)

            if (!organization) {
                this.reportMissingOrganization()

                return
            }

            const state = flag.enabled ? 'on' : 'off'

            openModal(ConfirmModal, {
                label: 'Are you sure?',
                text:
                    `Deleting the ${this.humanize(flag.name)} override for ` +
                    `${organization.name} will return that organization to ` +
                    `the global setting, ${state}.`,
                confirm: () => {
                    closeModal()
                    this.deleteOverride(flag, {
                        organizationId,
                        focusAfter: focusAfter ?? this.neighbourAfterDelete(flag, organizationId),
                    })
                },
                cancel: () => {
                    closeModal()
                },
            })
        },

        confirmDeleteSelectedOverride({ baseName }) {
            if (!this.selectedOrganization) {
                return
            }

            this.confirmDeleteOverride(this.flagFor(baseName), {
                organizationId: this.selectedOrganization.id,

                // The row's own toggle: same row, and always rendered.
                focusAfter: [`org-flag-toggle-${baseName}`],
            })
        },

        // Adding an override is as scoped and as reversible as deleting one,
        // so it prompts too rather than writing on the first click.
        confirmAddOverride(flag, { organizationId }) {
            const organization = this.organizationFor(organizationId)

            if (!organization) {
                this.reportMissingOrganization()

                return
            }

            // Adding an override always lands on the opposite of the current
            // global — that is what makes the row diverge, and so what makes
            // the endpoint report it as an override.
            const state = flag.enabled ? 'off' : 'on'

            openModal(ConfirmModal, {
                label: 'Are you sure?',
                text:
                    `Adding a ${this.humanize(flag.name)} override for ` +
                    `${organization.name} will turn it ${state} for that ` +
                    'organization only.',
                confirm: () => {
                    closeModal()
                    this.addOverride(flag, { organizationId })
                },
                cancel: () => {
                    closeModal()
                },
            })
        },

        confirmSelectedOrganizationFlag({ baseName, enabled }) {
            const organization = this.selectedOrganization

            if (!organization) {
                return
            }

            const action = enabled ? 'on' : 'off'

            openModal(ConfirmModal, {
                label: 'Are you sure?',
                text:
                    `Turning ${action} ${this.humanize(baseName)} for ` +
                    `${organization.name} will change it for that ` +
                    'organization only.',
                confirm: () => {
                    closeModal()
                    this.setSelectedOrganizationFlag({ baseName, enabled })
                },
                cancel: () => {
                    closeModal()
                },
            })
        },

        addOverride(flag, { organizationId }) {
            let succeeded = false

            if (this.isSaving || this.isBusy) {
                return Promise.resolve()
            }

            const organization = this.materializeOrganization(organizationId)

            if (!organization) {
                this.reportMissingOrganization()

                return Promise.resolve()
            }

            if (this.savingOrganizationIds(flag).includes(organizationId)) {
                return Promise.resolve()
            }

            const previous = this.overrideStateOf(organization, flag.name)
            const enabled = !flag.enabled
            const url = `organizations/${organizationId}/feature-flags/${this.matrix.flags[flag.name]}`

            this.applyOverrideState(organization, flag.name, { value: enabled, isOverride: true })
            this.startSaving(flag, organizationId)

            return this.$http
                .put(url, { value: enabled })
                .then(() => {
                    succeeded = true
                    this.toast(`${this.humanize(flag.name)} override added for ${organization.name}.`)
                })
                .catch(() => {
                    this.applyOverrideState(organization, flag.name, previous)
                    this.toast('There was a problem adding this override.', 'error')
                })
                .finally(() => {
                    this.stopSaving(flag, organizationId)

                    if (!succeeded) {
                        return null
                    }

                    // The Add button is gone once the form closes; land on the
                    // row that was just created. This has to run after
                    // stopSaving, or the new row's control is still disabled
                    // and gets skipped.
                    return this.focusFirstAvailable([
                        `override-delete-${flag.name}-${organizationId}`,
                        `override-add-toggle-${flag.name}`,
                    ])
                })
        },

        deleteOverride(flag, { organizationId, focusAfter = [] }) {
            let succeeded = false

            if (this.isSaving || this.isBusy) {
                return Promise.resolve()
            }

            const organization = this.materializeOrganization(organizationId)

            if (!organization) {
                this.reportMissingOrganization()

                return Promise.resolve()
            }

            if (this.savingOrganizationIds(flag).includes(organizationId)) {
                return Promise.resolve()
            }

            const previous = this.overrideStateOf(organization, flag.name)
            const url = `organizations/${organizationId}/feature-flags/${this.matrix.flags[flag.name]}`

            this.applyOverrideState(organization, flag.name, { value: flag.enabled, isOverride: false })
            this.startSaving(flag, organizationId)

            return this.$http
                .delete(url)
                .then(() => {
                    succeeded = true
                    this.toast(`${this.humanize(flag.name)} override removed for ${organization.name}.`)
                })
                .catch(() => {
                    this.applyOverrideState(organization, flag.name, previous)
                    this.toast('There was a problem removing this override.', 'error')
                })
                .finally(() => {
                    this.stopSaving(flag, organizationId)

                    // After stopSaving, or the neighbour is still disabled by
                    // the in-flight write and gets skipped.
                    return succeeded ? this.focusFirstAvailable(focusAfter) : null
                })
        },

        // Always PUT. Pennant writes a row on the next read anyway, so
        // deleting one to express "same as global" buys nothing. A row equal
        // to the global is simply not reported as an override, which is what
        // matchesGlobal drives below.
        setSelectedOrganizationFlag({ baseName, enabled }) {
            if (this.isSaving || this.isBusy) {
                return Promise.resolve()
            }

            if (this.selectedOrganizationId === null) {
                return Promise.resolve()
            }

            const organization = this.materializeOrganization(this.selectedOrganizationId)

            if (!organization) {
                this.reportMissingOrganization()

                return Promise.resolve()
            }

            const flag = { name: baseName }

            if (this.savingOrganizationIds(flag).includes(organization.id)) {
                return Promise.resolve()
            }

            const globalValue = this.globalFlagValues[baseName]
            const matchesGlobal = globalValue !== undefined && enabled === globalValue
            const previous = this.overrideStateOf(organization, baseName)
            const url = `organizations/${organization.id}/feature-flags/${this.matrix.flags[baseName]}`

            this.applyOverrideState(organization, baseName, {
                value: enabled,
                isOverride: !matchesGlobal,
            })
            this.startSaving(flag, organization.id)

            return this.$http
                .put(url, { value: enabled })
                .then(() => {
                    const state = enabled ? 'on' : 'off'

                    this.toast(`${this.humanize(baseName)} is now ${state} for ${organization.name}.`)
                })
                .catch(() => {
                    this.applyOverrideState(organization, baseName, previous)
                    this.toast('There was a problem updating this organization feature flag.', 'error')
                })
                .finally(() => {
                    this.stopSaving(flag, organization.id)

                    // ConfirmModal left focus alone while the toggle was
                    // disabled by the in-flight write. The toggle survives
                    // either outcome, so this is not gated on success.
                    return this.focusFirstAvailable([`org-flag-toggle-${baseName}`])
                })
        },

        startSaving(flag, organizationId) {
            this.savingCells = {
                ...this.savingCells,
                [flag.name]: [...this.savingOrganizationIds(flag), organizationId],
            }
        },

        stopSaving(flag, organizationId) {
            this.savingCells = {
                ...this.savingCells,
                [flag.name]: this.savingOrganizationIds(flag).filter((id) => id !== organizationId),
            }
        },

        loadFlags() {
            this.isLoading = true
            this.loadError = null

            return this.$http
                .get('feature-flags/global')
                .then(({ data }) => {
                    this.flags = data
                })
                .catch(() => {
                    const message = 'There was a problem loading feature flags.'

                    this.loadError = message
                    this.toast(message, 'error')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        confirmToggle(flag, enabled) {
            const action = enabled ? 'on' : 'off'
            const effect = enabled ? 'enable' : 'disable'

            openModal(ConfirmModal, {
                label: 'Are you sure?',
                text:
                    `Turning ${action} ${this.humanize(flag.name)} will ` +
                    `${effect} it for every organization, overriding any ` +
                    `organization-specific setting.${this.overrideWarning(flag)}`,
                confirm: () => {
                    closeModal()
                    this.setFlag(flag, enabled)
                },
                cancel: () => {
                    closeModal()
                },
            })
        },

        overrideWarning(flag) {
            if (this.canViewOrganizationFlags && (this.isMatrixLoading || this.matrixError)) {
                return ' Any organization overrides will be cleared.'
            }

            const count = this.overrideCount(flag)

            if (count === 0) {
                return ''
            }

            const noun = count === 1 ? 'override' : 'overrides'

            return ` ${count} organization ${noun} will be cleared.`
        },

        setFlag(flag, enabled) {
            this.isSaving = true

            return this.$http
                .put(`feature-flags/global/${flag.name}`, { enabled })
                .then(({ data }) => {
                    const index = this.flags.findIndex((f) => f.name === data.name)

                    if (index !== -1) {
                        this.flags.splice(index, 1, data)
                    }

                    this.toast(`${this.humanize(data.name)} is now ${data.enabled ? 'on' : 'off'}.`)

                    if (this.canViewOrganizationFlags) {
                        return this.loadOrganizationFlags()
                    }

                    return null
                })
                .catch(() => {
                    this.toast('There was a problem updating this feature flag.', 'error')
                })
                .finally(() => {
                    this.isSaving = false

                    // isSaving disables every global toggle for the duration
                    // of the write, so focus is placed once it clears.
                    return this.focusFirstAvailable([`global-flag-${flag.name}`])
                })
        },

        humanize(name) {
            return name
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        },
    },
}
</script>

<style scoped lang="scss">
@use '@sass/_variables.scss' as *;

.section-help {
    margin-bottom: 24px;
    color: #c9c9c9;
    font-family: 'Avenir Book', sans-serif;
}

.section-error {
    color: #ffa8a2;
    font-family: 'Avenir Heavy', sans-serif;
}

.flag-list {
    > div:not(:last-child) {
        margin-bottom: 16px;
    }
}

.organization-filter {
    max-width: 420px;
    margin-bottom: 24px;
}

.organization-filter__label {
    display: block;
    margin-bottom: 8px;
    color: $text-muted;
    font-family: 'Avenir Heavy', sans-serif;
    font-size: 0.9125em;
}
</style>
